// Explorer date-window helpers. The Explorer always hands useConnect an
// EXPLICIT compare window whenever a custom range is active: the parameterized
// pipeline's 'yoy' mode pairs buckets by calendar identity, which collapses
// for ranges longer than 12 months (two buckets a year apart share a calendar
// key), so a custom range with no user-picked compare derives its year-back
// window here instead and flows through the offset-from-window-start pairing.

/** The Explorer's picked windows (absent = the default trailing window). */
export interface ExplorerRange {
  /** Current window [start, end], inclusive local dates. */
  dateRange: [Date, Date]
  /** User-picked compare window; omitted → autoYearBackCompare applies. */
  compareRange?: [Date, Date]
}

const MS_PER_DAY = 86_400_000

/**
 * The same-length window exactly one calendar year earlier: the start shifts
 * by `setFullYear(-1)` (calendar-year identity, matching the dashboard's
 * year-back construction), and the end preserves the current window's
 * inclusive day span — NOT its own year-back, so leap-year length drift can't
 * desync the offset-paired buckets.
 *
 * A range STARTING on Feb 29 has no calendar-year-back start; JS normalizes
 * `setFullYear(-1)` to Mar 1, so the derived window begins one day later than
 * the naive expectation. Accepted: still contiguous and span-preserving.
 */
export function autoYearBackCompare(range: [Date, Date]): [Date, Date] {
  const [start, end] = range
  const spanDays = Math.round((end.getTime() - start.getTime()) / MS_PER_DAY)
  const compareStart = new Date(start)
  compareStart.setFullYear(compareStart.getFullYear() - 1)
  const compareEnd = new Date(compareStart)
  compareEnd.setDate(compareEnd.getDate() + spanDays)
  return [compareStart, compareEnd]
}

/**
 * Parses the pipeline's local-serialized 'YYYY-MM-DD' window bounds back to a
 * local Date (`new Date(str)` would parse date-only strings as UTC and shift
 * the calendar day for viewers west of UTC).
 */
export function localDateFromStr(dateStr: string): Date {
  const [year, month, day] = dateStr.split("-").map(Number)
  return new Date(year, month - 1, day)
}
